import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import s from "../clinic.module.css";

function IconClose() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={s.clubLifeLightboxIcon}>
      <path
        d="M6 6l12 12M18 6L6 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconChevron({ dir }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={s.clubLifeLightboxIcon}>
      <path
        d={dir === "prev" ? "M14 6l-6 6 6 6" : "M10 6l6 6-6 6"}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ClubLifePhotoLightbox({
  images = [],
  index = 0,
  onClose,
  onChangeIndex,
}) {
  const [open, setOpen] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartX = useRef(null);
  const viewportRef = useRef(null);

  const goPrev = useCallback(() => {
    if (index > 0) onChangeIndex(index - 1);
  }, [index, onChangeIndex]);

  const goNext = useCallback(() => {
    if (index < images.length - 1) onChangeIndex(index + 1);
  }, [images.length, index, onChangeIndex]);

  const handleClose = useCallback(() => {
    setOpen(false);
    window.setTimeout(onClose, 220);
  }, [onClose]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    setDragOffset(0);
  }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [goNext, goPrev, handleClose]);

  const onPointerDown = (e) => {
    if (images.length <= 1) return;
    dragStartX.current = e.clientX;
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging || dragStartX.current == null) return;
    let delta = e.clientX - dragStartX.current;
    if (index <= 0 && delta > 0) delta *= 0.35;
    if (index >= images.length - 1 && delta < 0) delta *= 0.35;
    setDragOffset(delta);
  };

  const finishDrag = (clientX) => {
    if (dragStartX.current == null) return;
    const delta = clientX - dragStartX.current;
    dragStartX.current = null;
    setDragging(false);
    const width = viewportRef.current?.offsetWidth || 320;
    const threshold = Math.min(80, width * 0.18);
    if (delta < -threshold && index < images.length - 1) {
      setDragOffset(0);
      goNext();
      return;
    }
    if (delta > threshold && index > 0) {
      setDragOffset(0);
      goPrev();
      return;
    }
    setDragOffset(0);
  };

  const onPointerUp = (e) => {
    finishDrag(e.clientX);
  };

  const onPointerCancel = () => {
    dragStartX.current = null;
    setDragging(false);
    setDragOffset(0);
  };

  if (index == null || !images.length) return null;

  const trackStyle = {
    transform: `translate3d(calc(-${index * 100}% + ${dragOffset}px), 0, 0)`,
    transition: dragging
      ? "none"
      : "transform 0.38s cubic-bezier(0.22, 1, 0.36, 1)",
  };

  return createPortal(
    <div
      className={`${s.clubLifeLightbox} ${open ? s.clubLifeLightbox_open : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр фото"
    >
      <button
        type="button"
        className={s.clubLifeLightboxBackdrop}
        onClick={handleClose}
        aria-label="Закрыть"
      />

      <header className={s.clubLifeLightboxTop}>
        {images.length > 1 ? (
          <span className={s.clubLifeLightboxCounter}>
            {index + 1} из {images.length}
          </span>
        ) : (
          <span className={s.clubLifeLightboxCounter}>Фото</span>
        )}
        <button
          type="button"
          className={s.clubLifeLightboxClose}
          onClick={handleClose}
          aria-label="Закрыть"
        >
          <IconClose />
        </button>
      </header>

      <div
        ref={viewportRef}
        className={s.clubLifeLightboxViewport}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <div className={s.clubLifeLightboxTrack} style={trackStyle}>
          {images.map((url, i) => (
            <div key={`${url}-${i}`} className={s.clubLifeLightboxSlide}>
              <img
                src={url}
                alt=""
                draggable={false}
                className={
                  i === index ? s.clubLifeLightboxImgActive : s.clubLifeLightboxImg
                }
              />
            </div>
          ))}
        </div>

        {images.length > 1 ? (
          <>
            <button
              type="button"
              className={`${s.clubLifeLightboxNav} ${s.clubLifeLightboxNav_prev}`}
              disabled={index <= 0}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              aria-label="Предыдущее фото"
            >
              <IconChevron dir="prev" />
            </button>
            <button
              type="button"
              className={`${s.clubLifeLightboxNav} ${s.clubLifeLightboxNav_next}`}
              disabled={index >= images.length - 1}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              aria-label="Следующее фото"
            >
              <IconChevron dir="next" />
            </button>
          </>
        ) : null}
      </div>

      {images.length > 1 ? (
        <div className={s.clubLifeLightboxDots} aria-hidden>
          {images.map((url, i) => (
            <span
              key={`dot-${url}-${i}`}
              className={
                i === index
                  ? `${s.clubLifeLightboxDot} ${s.clubLifeLightboxDot_active}`
                  : s.clubLifeLightboxDot
              }
            />
          ))}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
